import React from 'react';
import { Typography } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import EntityManagementScreen from '../common/EntityManagementScreen';
import PatientUploadedFiles from '../../components/patient/PatientUploadedFiles.component';
import PatientConsentStatus from '../../components/patient/PatientConsentStatus.component';
import PatientDynamicForms from '../../components/clinical/patientForms/PatientDynamicForms.component';
import { PATIENT_FIELDS_TO_HIDE } from '../../constants/patientFormFields';

const { Title, Paragraph, Text } = Typography;

const patientDisplayName = (patient) => {
  if (!patient) return '';
  const persisted = (patient.fullName || '').trim();
  if (persisted) return persisted;
  return [patient.firstName, patient.middleName, patient.lastName]
    .filter((n) => n && String(n).trim())
    .join(' ')
    .trim();
};

const PatientHelpPanel = ({ t }) => (
  <>
    <Title level={4}>{t('Patient record')}</Title>
    <Paragraph type="secondary" style={{ marginBottom: 12 }}>
      {t('Identifiers are optional. When supplied they are checked for format and checksum.')}
    </Paragraph>
    <Paragraph style={{ marginBottom: 8 }}>
      <Text strong>{t('NHS Number')}</Text>
      <br />
      <Text type="secondary">{t('10 digits with a modulus-11 check digit (spaces allowed).')}</Text>
    </Paragraph>
    <Paragraph style={{ marginBottom: 8 }}>
      <Text strong>{t('CHI Number')}</Text>
      <br />
      <Text type="secondary">{t('Scottish CHI: 10 digits, first 6 = date of birth (DDMMYY), modulus-11 check.')}</Text>
    </Paragraph>
    <Paragraph style={{ marginBottom: 8 }}>
      <Text strong>{t('Postcode')}</Text>
      <br />
      <Text type="secondary">{t('UK postcode format (BS 7666), including GIR 0AA. Space optional.')}</Text>
    </Paragraph>
    <Paragraph style={{ marginBottom: 16 }}>
      <Text strong>{t('Date of birth')}</Text>
      <br />
      <Text type="secondary">{t('Must be a real calendar date, not in the future, and result in an age of 18 or over.')}</Text>
    </Paragraph>

    <Title level={5} style={{ marginTop: 8, marginBottom: 8 }}>{t('Patient forms')}</Title>
    <Paragraph type="secondary" style={{ marginBottom: 16 }}>
      {t('Clinical questionnaires for this patient (e.g. AF baseline and follow-up). Open a published form, fill answers, and save as draft or completed. Completed responses show as Active; drafts as Inactive. You can filter, reopen, or delete responses from the list.')}
    </Paragraph>

    <Title level={5} style={{ marginTop: 8, marginBottom: 8 }}>{t('Uploaded Files')}</Title>
    <Paragraph type="secondary" style={{ marginBottom: 0 }}>
      {t('Study ZIPs are converted to .mat via NiFi. PDF and DICOM files are stored as-is and marked completed. Search by filename, path or type; delete removes the batch and its files.')}
    </Paragraph>
  </>
);

// Handles both /patients/create (create) and /patients/:patientId (view/edit), mirroring the
// SubjectDetailScreen split. Uploaded files depend on a real UploadBatch.PatientId, so they
// only render once we're on a route with an actual patientId — never on the create page.
const PatientDetailScreen = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isCreate = !patientId;

  return (
    <EntityManagementScreen
      entityName="Patient"
      beautifulName="Patient"
      fieldsToHide={PATIENT_FIELDS_TO_HIDE}
      showEntityEditById={patientId || null}
      showCreateForm={isCreate}
      showListByDefault={false}
      // This screen owns Create/View/Edit as separate routes already — the inline "switch to
      // Create" button DynamicFormSP would otherwise show on the edit page makes no sense here.
      enableCreate={false}
      onBackClick={() => navigate('/patients')}
      sectionKicker={t('Patient')}
      sectionTitle={(patient) => patientDisplayName(patient) || (isCreate ? t('New patient') : t('Patient'))}
      helpContent={<PatientHelpPanel t={t} />}
      renderDetailExtra={isCreate ? null : (id) => (
        <>
          <PatientConsentStatus patientId={id} />
          <PatientDynamicForms patientId={id} />
          <PatientUploadedFiles patientId={id} />
        </>
      )}
      onEntityReady={isCreate ? (entity) => {
        if (entity?.id) navigate(`/patients/${entity.id}`, { replace: true });
      } : undefined}
    />
  );
};

export default PatientDetailScreen;
